import React, { useEffect, useState } from "react";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { HERO_SLIDES } from "../../lib/constants";

const SLIDE_INTERVAL = 6000;

export const Hero = () => {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Preload all slides so transitions are instant
    HERO_SLIDES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % HERO_SLIDES.length),
      SLIDE_INTERVAL
    );
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden flex items-center justify-center"
    >
      {/* Slideshow layers */}
      <div className="absolute inset-0">
        {HERO_SLIDES.map((src, i) => (
          <div
            key={src}
            data-testid={`hero-slide-${i}`}
            className={`absolute inset-0 transition-opacity duration-[1600ms] ease-in-out ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={src}
              alt=""
              className={`w-full h-full object-cover ${
                i === index ? "hero-slide-active" : ""
              }`}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/30 to-black/55" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
        <p
          data-testid="hero-overline"
          className="overline text-white/85 fade-in delay-100"
          style={{ color: "#E4D2B5" }}
        >
          {t.hero.overline}
        </p>
        <h1
          data-testid="hero-title"
          className="font-serif text-[2.6rem] sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.04] tracking-tight font-light mt-7 fade-up delay-200"
        >
          {t.hero.title1}
          <br />
          <span className="italic text-gold">{t.hero.title2}</span>
        </h1>
        <p
          data-testid="hero-subtitle"
          className="mt-8 max-w-2xl mx-auto text-base md:text-lg text-white/85 font-light leading-relaxed fade-up delay-300"
        >
          {t.hero.subtitle}
        </p>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 fade-up delay-400">
          <button
            data-testid="hero-cta-book"
            onClick={() => scrollTo("booking")}
            className="btn-luxury"
          >
            {t.hero.cta1}
          </button>
        </div>
      </div>

      {/* Slide indicators */}
      <div
        data-testid="hero-indicators"
        className="absolute bottom-24 md:bottom-20 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2"
      >
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-indicator-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => setIndex(i)}
            className={`h-[2px] transition-all duration-500 ${
              i === index
                ? "w-10 bg-white"
                : "w-6 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      <button
        onClick={() => scrollTo("trust")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white transition-colors flex flex-col items-center gap-2 fade-in delay-700"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">
          {t.hero.scroll}
        </span>
        <ArrowDown size={16} className="animate-bounce" />
      </button>
    </section>
  );
};
